import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

// Catch render-time errors so a bug in a single screen doesn't leave the user
// staring at a white page. Shows the actual error so it's debuggable.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }
  reset = () => { this.setState({ error: null }); window.location.reload(); };
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="screen" style={{ padding: 24 }}>
        <div className="empty-state">
          <div className="empty-icon">⚠</div>
          <div className="empty-title">что-то сломалось</div>
          <div className="empty-sub" style={{ maxWidth: 600, margin: '8px auto 16px', textAlign: 'left', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, background: 'var(--paper-2)', padding: 12, borderRadius: 8 }}>
            {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
          </div>
          <button className="btn btn-dark" onClick={this.reset}>перезагрузить</button>
        </div>
      </div>
    );
  }
}
import { EpisodesScreen } from './screens/EpisodesScreen.jsx';
import { ChaptersScreen } from './screens/ChaptersScreen.jsx';
import { EditorScreen } from './screens/EditorScreen.jsx';
import { ScheduleScreen } from './screens/ScheduleScreen.jsx';
import { RendersScreen } from './screens/RendersScreen.jsx';
import { NewClipScreen } from './screens/NewClipScreen.jsx';
import { TranscriptScreen } from './screens/TranscriptScreen.jsx';
import { SettingsScreen } from './screens/SettingsScreen.jsx';
import { LoginScreen } from './screens/LoginScreen.jsx';
import { useStore } from './state/store.js';
import { api, onUnauthorized } from './lib/api.js';

function TopNav() {
  const navigate = useNavigate();
  const loc = useLocation();
  const activeTab =
    loc.pathname.startsWith('/schedule') ? 'schedule' :
    loc.pathname.startsWith('/renders') ? 'renders' : 'episodes';

  const logout = async () => {
    try { await api.logout(); } catch {}
    navigate('/login', { replace: true });
  };

  return (
    <header className="topnav">
      <div className="topnav-left" onClick={() => navigate('/episodes')} role="button">
        <div className="logo-mark">
          <svg viewBox="0 0 32 32" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="10" cy="16" r="6" />
            <circle cx="22" cy="16" r="6" />
          </svg>
        </div>
        <div className="logo-text">
          <span className="logo-text-main">Dos Mundos</span>
          <span className="logo-text-sub">Reels · CMS</span>
        </div>
      </div>
      <nav className="topnav-tabs">
        <button className={'tab ' + (activeTab === 'episodes' ? 'tab-active' : '')} onClick={() => navigate('/episodes')}>Эпизоды</button>
        <button className={'tab ' + (activeTab === 'renders' ? 'tab-active' : '')} onClick={() => navigate('/renders')}>Рендер</button>
        <button className={'tab ' + (activeTab === 'schedule' ? 'tab-active' : '')} onClick={() => navigate('/schedule')}>Расписание</button>
      </nav>
      <div className="topnav-right">
        <button className="btn-link small" onClick={logout}>выйти</button>
      </div>
    </header>
  );
}

function Toaster() {
  const toasts = useStore((s) => s.toasts);
  const dismiss = useStore((s) => s.dismissToast);
  return (
    <div className="toaster">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`} onClick={() => dismiss(t.id)}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const editorLayout = /^\/clip\//.test(pathname);
  const transcriptLayout = /^\/episode\/[^/]+$/.test(pathname);
  const appClass = [
    'app',
    editorLayout ? 'app--editor' : '',
    transcriptLayout ? 'app--transcript' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={appClass}>
      <TopNav />
      {children}
      <Toaster />
    </div>
  );
}

function Protected({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState('checking'); // checking | authed | guest

  useEffect(() => {
    let alive = true;
    api.authStatus()
      .then((r) => { if (alive) setState(r.authenticated ? 'authed' : 'guest'); })
      .catch(() => { if (alive) setState('guest'); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (state === 'guest') {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }
  }, [state, navigate, location.pathname]);

  if (state === 'checking') {
    return (
      <Shell>
        <div className="empty-state"><div className="empty-icon">◇</div><div className="empty-title">проверка авторизации…</div></div>
      </Shell>
    );
  }
  if (state === 'guest') return null;
  return <Shell><ErrorBoundary>{children}</ErrorBoundary></Shell>;
}

export default function App() {
  const navigate = useNavigate();
  const startRealtime = useStore((s) => s.startRealtime);
  const loadTopics = useStore((s) => s.loadTopics);

  useEffect(() => {
    loadTopics().catch(() => {});
    startRealtime();
    const off = onUnauthorized(() => navigate('/login', { replace: true }));
    return () => {
      useStore.getState().stopRealtime();
      off();
    };
  }, [loadTopics, startRealtime, navigate]);

  return (
    <Routes>
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/" element={<Navigate to="/episodes" replace />} />
      <Route path="/episodes" element={<Protected><EpisodesScreen /></Protected>} />
      <Route path="/episode/:epId" element={<Protected><TranscriptScreen /></Protected>} />
      <Route path="/episode/:epId/chapters" element={<Protected><ChaptersScreen /></Protected>} />
      <Route path="/episode/:epId/new-clip" element={<Protected><NewClipScreen /></Protected>} />
      <Route path="/episode/:epId/transcript" element={<Navigate to=".." replace />} />
      <Route path="/clip/:clipId" element={<Protected><EditorScreen /></Protected>} />
      <Route path="/schedule" element={<Protected><ScheduleScreen /></Protected>} />
      <Route path="/renders" element={<Protected><RendersScreen /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsScreen /></Protected>} />
      <Route path="*" element={<Navigate to="/episodes" replace />} />
    </Routes>
  );
}
